import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const { Box, Title, Table, Button, Pagination, Select, TextField } = vp;

type BatchFilesProps = {
  batchFiles: { [key: number]: vp.BatchFileTransactionsList };
};

type BatchFileListType = {
  [key: number]: vp.BatchFileTransactionsList;
};

type LocationProps = {
  service: string;
};

type FC = FunctionComponent<BatchFilesProps>;

const BatchFileLists: FC = (props: BatchFilesProps) => {
  const { batchFiles } = props;
  const { state } = useLocation<LocationProps>();
  const [filteredLists, setFilteredLists] = useState<BatchFileListType>({});
  const [status, setStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // pagination
  const [currentPage, setCurrentPage] = useState(0);
  const PER_PAGE = 20;
  const offset = currentPage * PER_PAGE;
  const pageCount = Math.ceil(Object.values(filteredLists).length / PER_PAGE) || 0;

  const statusOptions = {
    All: 'All',
    requested: 'Requested',
    pending: 'Pending',
    'in Progress': 'In Progress',
    sent: 'Sent',
    successful: 'Successful',
    settled: 'Settled',
    failed: 'Failed',
    cancelled: 'Cancelled',
    'cancellation requested': 'Cancellation Requested',
    waiting: 'Waiting',
  };

  const handlePageClick = ({ selected: selectedPage }: { [idx: string]: number }) => {
    setCurrentPage(selectedPage);
  };

  // search form
  useEffect(() => {
    setFilteredLists(batchFiles || {});
  }, []);

  useEffect(() => {
    const keys = ['FirstName', 'LastName'];
    setFilteredLists(
      Object.values(batchFiles || {}).filter(
        (data: vp.BatchFileTransactionsList) =>
          (status === '' || status === 'All' || data.Status == status) &&
          (searchTerm === '' ||
            keys.some((key) =>
              data[key as keyof vp.BatchFileTransactionsList]
                ?.toString()
                .toLowerCase()
                .includes(searchTerm.toLowerCase()),
            )),
      ),
    );
    setCurrentPage(0);
  }, [status, searchTerm]);

  const tableProps: vp.TableProps = {
    id: 'transactionHistoryTable',
    name: 'transactions',
    columns: {
      id: { header: 'ID', align: 'left' },
      name: { header: 'Name', align: 'left' },
      dateAdded: { header: 'Date Added', align: 'left' },
      lastModified: { header: 'Last Modified', align: 'left' },
      amount: { header: 'Amount', align: 'left' },
      status: { header: 'Status' },
      view: { header: '' },
    },
    rows:
      Object.values(filteredLists)
        .slice(offset, offset + PER_PAGE)
        .map((transaction: vp.BatchFileTransactionsList, index) => ({
          id: transaction?.ID,
          name: (
            <>
              {transaction?.FirstName} {transaction?.LastName}
            </>
          ),
          dateAdded: transaction?.DateAdded,
          lastModified: transaction?.LastModified,
          amount: <>{`$${transaction?.Amount}`}</>,
          status: transaction?.Status,
          view: (
            <Link
              to={{
                pathname: `/transactions-history/details/${state.service}/${transaction.VIOAccountTransactions_ID}/CAD`,
                state: { transactionId: transaction.VIOAccountTransactions_ID },
              }}
            >
              <Button id={`transaction-details-${index}`} label="Details" color="lined" noShadow />
            </Link>
          ),
        })) || [],
  };

  return (
    <Box flx="col">
      <Box mb="md" jc="sb">
        <Title title="Transactions" />
      </Box>
      <Box mt="sm">
        <Box gap="sm" wrap="wrap">
          <Box width="200px">
            <Select
              placeholder="Status"
              options={statusOptions}
              name="status"
              value={status}
              onBlur={() => {}}
              onChange={(e) => {
                setStatus(e.target.value);
              }}
              variant="outlined"
              noOrdering
            />
          </Box>
          <Box>
            <TextField
              key="TransactionSearchKeyword"
              name="TransactionSearchKeyword"
              placeholder="Keyword"
              value={searchTerm}
              onBlur={() => {}}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              variant="outlined"
              height="37px"
            />
          </Box>
        </Box>
      </Box>
      <Box mt="lg">
        <Table {...tableProps} />
      </Box>
      <Box width="100%" jc="center" mt="lg">
        {pageCount > 1 && (
          <Pagination pageCount={pageCount} forcePageNumber={currentPage} handlePageClick={handlePageClick} />
        )}
      </Box>
    </Box>
  );
};

export default BatchFileLists;
